import type { ShouldRevalidateFunction } from '@remix-run/react';

import { isContentPageRoute, isMessagesRoute } from '~/constants/community';

export const shouldRevalidateLoaderData: ShouldRevalidateFunction = ({
  nextUrl,
  currentUrl,
  defaultShouldRevalidate,
  formAction,
  formData,
}) => {
  const isCreatingLogin = formAction === '/create_login';

  const isRequestingMagicLink = formData?.get('request_magic_link') === 'true';

  if (isCreatingLogin && isRequestingMagicLink) {
    return false;
  }

  if (!isCreatingLogin) {
    const currentModalParamValue = currentUrl.searchParams.get('modal');
    const nextModalParamValue = nextUrl.searchParams.get('modal');
    const currentTabParamValue = currentUrl.searchParams.get('tab');
    const nextTabParamValue = nextUrl.searchParams.get('tab');
    const currentViewParamValue = currentUrl.searchParams.get('view');
    const nextViewParamValue = nextUrl.searchParams.get('view');
    const currentConnectedParamValue = currentUrl.searchParams.get('connected');
    const nextConnectedParamValue = nextUrl.searchParams.get('connected');
    const currentIntentParamValue = currentUrl.searchParams.get('intent');
    const nextIntentParamValue = nextUrl.searchParams.get('intent');
    const currentBillingErrorParamValue = currentUrl.searchParams.get('billing_portal_error');
    const nextBillingErrorParamValue = nextUrl.searchParams.get('billing_portal_error');
    const currentFilterParamValue = currentUrl.searchParams.get('filter');
    const nextFilterParamValue = nextUrl.searchParams.get('filter');
    const currentTagsParamValue = currentUrl.searchParams.getAll('tags').join();
    const nextTagsParamValue = nextUrl.searchParams.getAll('tags').join();

    const isPathnameTheSame = currentUrl.pathname === nextUrl.pathname;

    // Modal opening
    if (nextModalParamValue && isPathnameTheSame) {
      return false;
    }

    // Modal closing
    if (currentModalParamValue && !nextModalParamValue && isPathnameTheSame) {
      return false;
    }

    // Switching tabs
    if (nextTabParamValue && isPathnameTheSame) {
      return false;
    }

    // Going back to "manage"
    if (currentTabParamValue && !nextTabParamValue && isPathnameTheSame) {
      return false;
    }

    // Switching community feed view (?view=for_you|following)
    if (nextViewParamValue && isPathnameTheSame) {
      return false;
    }

    // Returning to default community feed view (clearing ?view=)
    if (currentViewParamValue && !nextViewParamValue && isPathnameTheSame) {
      return false;
    }

    // Switching the community messages inbox filter (?filter=unread|archived); no loader reads it there
    if (currentFilterParamValue !== nextFilterParamValue && isPathnameTheSame && isMessagesRoute(nextUrl.pathname)) {
      return false;
    }

    // Filtering a content page by tag (?tags=); the page filters the items it already loaded
    if (currentTagsParamValue !== nextTagsParamValue && isPathnameTheSame && isContentPageRoute(nextUrl.pathname)) {
      return false;
    }

    // Cleaning up post-OAuth connected provider params
    if (currentConnectedParamValue && !nextConnectedParamValue && isPathnameTheSame) {
      return false;
    }

    // Cleaning up intent params (deeplinks for modal promotion)
    if (currentIntentParamValue && !nextIntentParamValue && isPathnameTheSame) {
      return false;
    }

    // Cleaning up billing portal error params
    if (currentBillingErrorParamValue && !nextBillingErrorParamValue && isPathnameTheSame) {
      return false;
    }
  }

  return defaultShouldRevalidate;
};
